import { observer } from "mobx-react-lite";
import { Box, Button, Divider, ListSubheader, Popover, SvgIcon } from "@mui/material";
import React from "react";
import { SortState, TableState } from "./DataTable";
import { ListSortableField } from "../../modules/core/data/ListData";
import * as yup from "yup";
import { Form, Formik } from "formik";
import { FormAutocompleteField } from "../form/FormComponents";
import { PopoverState, usePopoverState } from "../popover/PopoverService";
import { FaSortAmountDown, FaSortAmountUp } from "react-icons/fa";
import { useGlobalTableSortFilterState } from "./TableFilterState";
import { useTranslation } from "react-i18next";
import { appI18n } from "../../modules/i18n/i18n";

const useSortStyles = () => ({
    popover: {
        width: "300",
    },
});

export enum SORT_ORDER {
    ASCENDING,
    DESCENDING,
}

interface QueryTableSortProps<RowData> {
    refetchQuery?: () => void;
    queryKey?: string;
}

export const QueryTableSort = observer(<RowData,>(props: QueryTableSortProps<RowData>) => {
    const { refetchQuery, queryKey } = props;
    const sortPopoverState = usePopoverState();
    const globalSortFilterState = useGlobalTableSortFilterState();
    const tableSortFilterState = globalSortFilterState.tableStateMap.get(queryKey);
    const { t } = useTranslation();

    if (!queryKey) {
        return null;
    }

    const _renderSortButton = () => {
        const icon = tableSortFilterState.sortState?.descending.value ? (
            <SvgIcon>
                <FaSortAmountDown />
            </SvgIcon>
        ) : (
            <SvgIcon>
                <FaSortAmountUp />
            </SvgIcon>
        );
        return (
            <Button color={"inherit"} startIcon={icon} onClick={sortPopoverState.open}>
                {t("common:table.sortByField", { field: tableSortFilterState.sortState?.field.label })}
            </Button>
        );
    };

    return (
        <>
            <Box display={"flex"} justifyContent={"flex-end"}>
                {_renderSortButton()}
            </Box>
            <TableSortPopover popoverState={sortPopoverState} refetchQuery={refetchQuery} queryKey={queryKey} />
        </>
    );
});

// ======================
// TableSortPopover
// ======================

interface TableSortPopoverProps<RowData> {
    popoverState: PopoverState;
    refetchQuery?: () => void;
    queryKey?: string;
}

const TableSortPopover = observer(<RowData,>(props: TableSortPopoverProps<RowData>) => {
    const { popoverState, refetchQuery, queryKey } = props;

    const styles = useSortStyles();
    const { t } = useTranslation();

    return (
        <Popover
            open={popoverState.isOpen}
            onClose={popoverState.close}
            anchorEl={popoverState.anchorEl}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
            }}
            sx={styles.popover}
        >
            <Box width={300}>
                <ListSubheader>{t("common:table.sortBy")}</ListSubheader>
                <TableSortForm closeForm={popoverState.close} refetchQuery={refetchQuery} queryKey={queryKey} />
            </Box>
        </Popover>
    );
});

// ======================
// TableSortForm
// ======================

interface TableSortFormProps<RowData> {
    queryKey?: string;
    closeForm: () => void;
    refetchQuery?: () => void;
}

export const TableSortForm = observer(<RowData,>(props: TableSortFormProps<RowData>) => {
    const { queryKey, closeForm, refetchQuery } = props;
    const globalSortFilterState = useGlobalTableSortFilterState();
    const tableSortFilterState = globalSortFilterState.tableStateMap.get(queryKey);
    const { t } = useTranslation();

    const schema = yup.object({
        field: yup.object({
            label: yup.string().required(t("common:table.validation.selectCriteria")),
            fieldId: yup.mixed(),
        }),
        descending: yup.object({
            label: yup.string().required(t("common:table.validation.selectSortOrder")),
            value: yup.boolean(),
        }),
    });

    const _getInitialValues = (): SortState => {
        const defaultValue = tableSortFilterState.sortFilterConfig.sort.defaultSort;
        if (!!tableSortFilterState.sortState) {
            return tableSortFilterState.sortState;
        } else {
            return {
                field: {
                    label: defaultValue.label,
                    fieldId: defaultValue.fieldId,
                },
                descending: {
                    label: getSortOrderLabel(SORT_ORDER.ASCENDING),
                    value: tableSortFilterState.sortFilterConfig.sort.defaultSortDesc,
                },
            };
        }
    };

    const sortOptions = tableSortFilterState.sortFilterConfig.sort.sortFields;

    return (
        <Formik
            initialValues={_getInitialValues()}
            validationSchema={schema}
            enableReinitialize
            onSubmit={async (values) => {
                globalSortFilterState.setSort(queryKey, values);
                closeForm();
                refetchQuery();
            }}
        >
            {(props) => {
                return (
                    <Form>
                        <Box p={2}>
                            <FormAutocompleteField<ListSortableField, false, true, false>
                                label={t("common:table.criteria")}
                                name={"field"}
                                options={sortOptions || []}
                                errorGetter={(v: ListSortableField) => v.label}
                                getOptionLabel={(o: ListSortableField) => o.label}
                                disableClearable
                                blurOnSelect
                            />
                            <br />
                            <FormAutocompleteField<SortSelectionType, false, true, false>
                                label={t("common:table.sortOrder")}
                                name={"descending"}
                                options={getSortOrderList()}
                                errorGetter={(v: SortSelectionType) => v.label}
                                getOptionLabel={(o: SortSelectionType) => o.label}
                                disableClearable
                                blurOnSelect
                            />
                        </Box>
                        <Divider />
                        <Box display={"flex"} justifyContent={"space-between"} p={2}>
                            <Button
                                variant={"outlined"}
                                onClick={() => {
                                    globalSortFilterState.clearSort(queryKey);
                                    props.handleReset();
                                }}
                            >
                                {t("common:actions.reset")}
                            </Button>
                            <Button variant={"contained"} color={"primary"} type={"submit"}>
                                {t("common:actions.apply")}
                            </Button>
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
});

export const getSortOrderLabel = (sortOrder: SORT_ORDER) => {
    if (sortOrder === SORT_ORDER.ASCENDING) {
        return appI18n.t("common:table.ascending");
    } else if (sortOrder === SORT_ORDER.DESCENDING) {
        return appI18n.t("common:table.descending");
    }
};

interface SortSelectionType {
    label: string;
    value: boolean;
}

const getSortOrderList = (): SortSelectionType[] => [
    {
        label: getSortOrderLabel(SORT_ORDER.ASCENDING),
        value: false,
    },
    {
        label: getSortOrderLabel(SORT_ORDER.DESCENDING),
        value: true,
    },
];
